import { useFieldArray, useWatch, Control, UseFormSetValue } from "react-hook-form";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Weight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { CreateRatingFormData } from "@/components/backOffice/ratings/rating.form";

interface RatingPackageFieldsProps {
  control: Control<CreateRatingFormData>;
  setValue: UseFormSetValue<CreateRatingFormData>;
}

export const RatingPackageFields = ({ control, setValue }: RatingPackageFieldsProps) => {
  const { t } = useTranslation("boRatings");
  const { fields, append, remove } = useFieldArray({
    control,
    name: "packages",
  });

  const packages = useWatch({ control, name: "packages" });

  const totalWeight = useMemo(
    () => (packages ?? []).reduce((sum, pkg) => sum + (Number(pkg?.weight) || 0), 0),
    [packages],
  );

  useEffect(() => {
    setValue("totalWeight", totalWeight);
  }, [totalWeight, setValue]);

  const handleAdd = () => {
    append({
      weight: undefined as unknown as number,
      length: undefined as unknown as number,
      width: undefined as unknown as number,
      height: undefined as unknown as number,
      value: undefined as unknown as number,
    });
  };

  return (
    <div className="@container flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{t("packageFields.title")}</h3>
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="mr-2 h-4 w-4" />
          {t("packageFields.addPackage")}
        </Button>
      </div>

      {fields.length === 0 && <p className="text-sm text-gray-500">{t("packageFields.empty")}</p>}

      {fields.map((field, index) => (
        <div
          key={field.id}
          className="flex flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50 p-4"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {t("packageFields.packageTitle", { number: index + 1 })}
            </span>
            {fields.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(index)}
                className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 @sm:grid-cols-3 @xl:grid-cols-5">
            <ControlledUnifiedInput
              control={control}
              name={`packages.${index}.weight`}
              label={t("packageFields.weightLabel")}
              inputType="float"
            />
            <ControlledUnifiedInput
              control={control}
              name={`packages.${index}.length`}
              label={t("packageFields.lengthLabel")}
              inputType="float"
            />
            <ControlledUnifiedInput
              control={control}
              name={`packages.${index}.width`}
              label={t("packageFields.widthLabel")}
              inputType="float"
            />
            <ControlledUnifiedInput
              control={control}
              name={`packages.${index}.height`}
              label={t("packageFields.heightLabel")}
              inputType="float"
            />
            <ControlledUnifiedInput
              control={control}
              name={`packages.${index}.value`}
              label={t("packageFields.insuranceLabel")}
              inputType="float"
            />
          </div>
        </div>
      ))}

      <div className="flex items-center gap-2 border-t border-gray-200 pt-3">
        <Weight className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {t("packageFields.totalWeightLabel")}
        </span>
        <span className="text-sm font-semibold">
          {t("packageFields.totalWeightValue", { weight: totalWeight })}
        </span>
      </div>
    </div>
  );
};
